import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import { OffersFilterId } from '../../OfferFilters.types';
import { OfferPageLayout } from '../../components/OfferPageLayout';
import { useOfferTable } from '../../hooks/useOfferTable';

import { usePopupsColumns } from './hooks/usePopupsColumns';

const TRIGGERED_POPUP_SUBTYPES = `${EPopupSubType.POST_PURCHASE},${EPopupSubType.STORE_REFRESH}`;

const PopupsTable = (): React.ReactElement => {
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const isTriggeredPopups = !!featureFlags?.[EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU];
  const offerLabel = isTriggeredPopups ? 'Triggered Popup' : 'Popup';
  const pageTitle = isTriggeredPopups ? 'Triggered Popups' : 'Popups';

  const {
    getOffers,
    pricingsResult,
    isPricingsLoading,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    hasActiveFilters,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    offers,
    processOffers
  } = useOfferTable({
    offerType: OfferType.POPUP,
    offerSubType: isTriggeredPopups ? TRIGGERED_POPUP_SUBTYPES : EPopupSubType.DAILY_BONUS,
    offerLabel,
    needsPricings: true
  });

  const { columns } = usePopupsColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    isPricingsLoading,
    pricingsResult,
    isTriggeredPopups
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title={pageTitle}
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel={pageTitle}
      showFilters={offers.length > 0}
      hasActiveFilters={hasActiveFilters}
      filterBarConfig={{
        hiddenFilters: isTriggeredPopups ? [OffersFilterId.OFFER_DESIGN] : []
      }}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle={`Add your first ${offerLabel}`}
      emptyStateDescription="Rewards that appear as popups when players open the webstore, encouraging them to return regularly"
      createButtonPrefix={offerLabel}
      bodyDataTestId="dashPopupsList"
      offerType={OfferType.POPUP}
      offerDesignType={OfferDesignTypes.PopUp}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle={`Delete ${offerLabel}`}
      deleteModalDescription={`Are you sure you want to delete this ${offerLabel.toLowerCase()}?`}
    />
  );
};

export { PopupsTable };
